<template>
    <div class="mt-5 space-y-6">
        <!-- Kafka Health Status -->
        <Card v-if="kafkaHealth">
            <CardHeader>
                <h3 class="text-lg font-semibold flex items-center gap-2">
                    <div :class="kafkaHealth.is_healthy ? 'w-3 h-3 bg-green-500 rounded-full' : 'w-3 h-3 bg-red-500 rounded-full'"></div>
                    Health Status
                </h3>
            </CardHeader>
            <CardContent>
                <TooltipProvider>
                    <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Status</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Overall health of the topic based on partition status</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-medium" :class="kafkaHealth.is_healthy ? 'text-green-600' : 'text-red-600'">
                                {{ kafkaHealth.is_healthy ? 'Healthy' : 'Unhealthy' }}
                            </p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Total Replicas</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Total number of partition replicas across all brokers</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-medium">{{ kafkaHealth.total_replicas || '-' }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">In-Sync Replicas</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Number of replicas that are caught up with the leader</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-medium">{{ kafkaHealth.in_sync_replicas || '-' }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Under-Replicated</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Partitions with fewer in-sync replicas than configured</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-medium" :class="kafkaHealth.under_replicated_partitions > 0 ? 'text-yellow-600' : ''">
                                {{ kafkaHealth.under_replicated_partitions || 0 }}
                            </p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Offline Partitions</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Partitions with no available leader replica</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-medium" :class="kafkaHealth.offline_partitions > 0 ? 'text-red-600' : ''">
                                {{ kafkaHealth.offline_partitions || 0 }}
                            </p>
                        </div>
                    </div>
                </TooltipProvider>
            </CardContent>
        </Card>

        <!-- Partition Details -->
        <Card v-if="kafkaPartitions">
            <CardHeader>
                <h3 class="text-lg font-semibold">Partition Details</h3>
            </CardHeader>
            <CardContent>
                <TooltipProvider>
                    <!-- Partition Summary Stats -->
                    <div class="p-4 bg-muted/50 rounded-lg mb-6">
                        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div class="flex flex-col">
                                <div class="flex items-center gap-1 mb-1">
                                    <span class="text-xs text-muted-foreground">Total Partitions</span>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p class="text-xs">Number of partitions this topic is divided into</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <span class="font-mono">{{ kafkaPartitions.count || '-' }}</span>
                            </div>
                            <div class="flex flex-col">
                                <div class="flex items-center gap-1 mb-1">
                                    <span class="text-xs text-muted-foreground">Replication Factor</span>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p class="text-xs">Number of replicas maintained for each partition</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <span class="font-mono">{{ kafkaPartitions.replication_factor || '-' }}</span>
                            </div>
                            <div class="flex flex-col">
                                <div class="flex items-center gap-1 mb-1">
                                    <span class="text-xs text-muted-foreground">Under-Replicated</span>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p class="text-xs">Count of partitions below target replication factor</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <span class="font-mono" :class="kafkaPartitions.under_replicated_count > 0 ? 'text-yellow-600' : ''">
                                    {{ kafkaPartitions.under_replicated_count || 0 }}
                                </span>
                            </div>
                            <div class="flex flex-col">
                                <div class="flex items-center gap-1 mb-1">
                                    <span class="text-xs text-muted-foreground">Offline Count</span>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p class="text-xs">Count of partitions with no available leader</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <span class="font-mono" :class="kafkaPartitions.offline_count > 0 ? 'text-red-600' : ''">
                                    {{ kafkaPartitions.offline_count || 0 }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Individual Partitions Table -->
                    <div v-if="kafkaPartitions.details?.length" class="space-y-3">
                        <div class="flex items-center gap-2">
                            <h4 class="font-medium">Individual Partitions</h4>
                            <Tooltip>
                                <TooltipTrigger>
                                    <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p class="text-xs">Detailed status of each partition and its replicas</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                        <div class="border rounded-md max-h-64 overflow-auto">
                            <Table>
                                <TableHeader class="sticky top-0 bg-background">
                                    <TableRow class="bg-muted/50">
                                        <TableHead>
                                            <Button
                                                variant="ghost"
                                                class="h-auto p-0 font-semibold hover:bg-transparent justify-start"
                                                @click="handleSort('partition_id')">
                                                <div class="flex items-center gap-1">
                                                    Partition ID
                                                    <component
                                                        :is="getSortIcon('partition_id')"
                                                        v-if="getSortIcon('partition_id')"
                                                        class="h-3 w-3" />
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p class="text-xs">Unique identifier for the partition</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button
                                                variant="ghost"
                                                class="h-auto p-0 font-semibold hover:bg-transparent justify-start"
                                                @click="handleSort('replicas')">
                                                <div class="flex items-center gap-1">
                                                    Replicas
                                                    <component
                                                        :is="getSortIcon('replicas')"
                                                        v-if="getSortIcon('replicas')"
                                                        class="h-3 w-3" />
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p class="text-xs">Broker IDs that hold copies of this partition</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <Button
                                                variant="ghost"
                                                class="h-auto p-0 font-semibold hover:bg-transparent justify-start"
                                                @click="handleSort('in_sync_replicas')">
                                                <div class="flex items-center gap-1">
                                                    In-Sync Replicas
                                                    <component
                                                        :is="getSortIcon('in_sync_replicas')"
                                                        v-if="getSortIcon('in_sync_replicas')"
                                                        class="h-3 w-3" />
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p class="text-xs">Replicas caught up with the leader</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </Button>
                                        </TableHead>
                                        <TableHead>
                                            <div class="flex items-center gap-1">
                                                Status
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p class="text-xs">Current health status of the partition</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow v-for="partition in sortedPartitions" :key="partition.partition_id">
                                        <TableCell class="font-mono">{{ partition.partition_id }}</TableCell>
                                        <TableCell class="font-mono text-sm">{{ partition.replicas?.join(', ') || '-' }}</TableCell>
                                        <TableCell class="font-mono text-sm">{{ partition.in_sync_replicas?.join(', ') || '-' }}</TableCell>
                                        <TableCell>
                                            <div class="flex items-center gap-2">
                                                <div v-if="partition.is_offline" class="flex items-center gap-1 text-red-600">
                                                    <div class="w-2 h-2 bg-red-500 rounded-full"></div>
                                                    <span class="text-sm">Offline</span>
                                                </div>
                                                <div v-else-if="partition.is_under_replicated" class="flex items-center gap-1 text-yellow-600">
                                                    <div class="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                                    <span class="text-sm">Under-replicated</span>
                                                </div>
                                                <div v-else class="flex items-center gap-1 text-green-600">
                                                    <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                                                    <span class="text-sm">Healthy</span>
                                                </div>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </TooltipProvider>
            </CardContent>
        </Card>

        <!-- Retention Configuration -->
        <Card v-if="kafkaConfigs">
            <CardHeader>
                <h3 class="text-lg font-semibold">Retention Configuration</h3>
            </CardHeader>
            <CardContent>
                <TooltipProvider>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Retention Time</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">How long messages are kept before deletion</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ formatKafkaConfig('retention_ms', kafkaConfigs.retention_ms) }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Retention Bytes</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Maximum size of log before old segments are deleted</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ formatKafkaConfig('retention_bytes', kafkaConfigs.retention_bytes) }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Delete Retention</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Time to retain delete tombstone markers</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ formatKafkaConfig('delete_retention_ms', kafkaConfigs.delete_retention_ms) }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Cleanup Policy</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Strategy for cleaning up old log segments (delete or compact)</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ kafkaConfigs.cleanup_policy || '-' }}</p>
                        </div>
                    </div>
                </TooltipProvider>
            </CardContent>
        </Card>

        <!-- Message & Compression Configuration -->
        <Card v-if="kafkaConfigs">
            <CardHeader>
                <h3 class="text-lg font-semibold">Message & Compression Configuration</h3>
            </CardHeader>
            <CardContent>
                <TooltipProvider>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Max Message Size</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Maximum size allowed for a single message</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ formatKafkaConfig('max_message_bytes', kafkaConfigs.max_message_bytes) }}</p>
                        </div>
                        <div class="space-y-1">
                            <Label class="text-sm text-muted-foreground">Compression Type</Label>
                            <p class="font-mono text-sm">{{ kafkaConfigs.compression_type || '-' }}</p>
                        </div>
                        <div class="space-y-1">
                            <div class="flex items-center gap-1">
                                <Label class="text-sm text-muted-foreground">Min In-Sync Replicas</Label>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <HelpCircle class="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p class="text-xs">Minimum replicas that must acknowledge a write</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                            <p class="font-mono text-sm">{{ kafkaConfigs.min_insync_replicas || '-' }}</p>
                        </div>
                        <div class="space-y-1">
                            <Label class="text-sm text-muted-foreground">Timestamp Type</Label>
                            <p class="font-mono text-sm">{{ kafkaConfigs.message_timestamp_type || '-' }}</p>
                        </div>
                    </div>
                </TooltipProvider>
            </CardContent>
        </Card>

        <!-- Segment Configuration -->
        <Card v-if="kafkaConfigs">
            <CardHeader>
                <h3 class="text-lg font-semibold">Segment Configuration</h3>
            </CardHeader>
            <CardContent>
                <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-1">
                        <Label class="text-sm text-muted-foreground">Segment Time</Label>
                        <p class="font-mono text-sm">{{ formatKafkaConfig('segment_ms', kafkaConfigs.segment_ms) }}</p>
                    </div>
                    <div class="space-y-1">
                        <Label class="text-sm text-muted-foreground">Segment Size</Label>
                        <p class="font-mono text-sm">{{ formatKafkaConfig('segment_bytes', kafkaConfigs.segment_bytes) }}</p>
                    </div>
                </div>
            </CardContent>
        </Card>

        <!-- Other Configuration -->
        <Card v-if="kafkaConfigs?.unclean_leader_election">
            <CardHeader>
                <h3 class="text-lg font-semibold">Other Configuration</h3>
            </CardHeader>
            <CardContent>
                <div class="space-y-1">
                    <Label class="text-sm text-muted-foreground">Unclean Leader Election</Label>
                    <p class="font-mono text-sm">{{ kafkaConfigs.unclean_leader_election || '-' }}</p>
                </div>
            </CardContent>
        </Card>

        <!-- Fallback message when no Kafka data is available -->
        <Card v-if="!kafkaHealth && !kafkaPartitions && !kafkaConfigs">
            <CardContent class="py-12">
                <div class="text-center space-y-2">
                    <AlertCircle class="h-12 w-12 text-muted-foreground mx-auto" />
                    <h3 class="text-lg font-medium">No Metadata Available</h3>
                    <p class="text-sm text-muted-foreground">
                        Topic metadata is not available for this topic. This may be due to the topic being inactive or not fully configured.
                    </p>
                </div>
            </CardContent>
        </Card>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { HelpCircle, AlertCircle, ChevronUp, ChevronDown } from 'lucide-vue-next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

/**
 * @typedef {Object} Props
 * @property {Object} topic - The topic object containing Kafka metadata
 * @property {Function} formatKafkaConfig - Function to format Kafka configuration values
 */
const props = defineProps({
    topic: {
        type: Object,
        default: () => ({})
    },
    formatKafkaConfig: {
        type: Function,
        required: true
    }
});

// Sorting state
const sortColumn = ref('partition_id');
const sortDirection = ref('asc');

// Computed properties for Kafka data - accessing the exact same structure as before
const kafkaHealth = computed(() => props.topic?.kafka?.health);
const kafkaPartitions = computed(() => props.topic?.kafka?.partitions);
const kafkaConfigs = computed(() => props.topic?.kafka?.configs);

// Sorted partitions
const sortedPartitions = computed(() => {
    if (!kafkaPartitions.value?.details || !Array.isArray(kafkaPartitions.value.details)) {
        return [];
    }

    const partitions = [...kafkaPartitions.value.details];
    
    return partitions.sort((a, b) => {
        let aValue = a[sortColumn.value];
        let bValue = b[sortColumn.value];
        
        // Handle special cases for array fields
        if (sortColumn.value === 'replicas' || sortColumn.value === 'in_sync_replicas') {
            aValue = Array.isArray(aValue) ? aValue.length : 0;
            bValue = Array.isArray(bValue) ? bValue.length : 0;
        }
        
        // Handle numeric sorting
        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection.value === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        // Handle string sorting
        aValue = String(aValue || '');
        bValue = String(bValue || '');
        
        const comparison = aValue.localeCompare(bValue);
        return sortDirection.value === 'asc' ? comparison : -comparison;
    });
});

// Sort function
const handleSort = (column) => {
    if (sortColumn.value === column) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortColumn.value = column;
        sortDirection.value = 'asc';
    }
};

// Sort icon component
const getSortIcon = (column) => {
    if (sortColumn.value !== column) return null;
    return sortDirection.value === 'asc' ? ChevronUp : ChevronDown;
};
</script>